import { useState } from 'react';
import './LayoutHandler.css';

export default function LayoutHandler({
  layout = {},
  selectedItem,
  onLayoutChange,
  configType
}) {
  const [selectedLayout, setSelectedLayout] = useState('');
  const [rangeMode, setRangeMode] = useState(false);
  const [rangeLow, setRangeLow] = useState('');
  const [rangeHigh, setRangeHigh] = useState('');

  // Regex to allow letters, numbers, %, { }, [ ], _, -
  const allowedPattern = /^[a-zA-Z0-9%\{\}\[\]_-]*$/;

  const handleSelectedLayoutChange = (e) => {
    const input = e.target.value;

    if (allowedPattern.test(input)) {
      setSelectedLayout(input);
    }
  };

  const handleAddToLayout = () => {
    if (!selectedItem) return;

    const updatedLayout = { ...layout };

    if (rangeMode) {
      const low = Number(rangeLow);
      const high = Number(rangeHigh);
      if (isNaN(low) || isNaN(high) || low > high) return;

      for (let slotNum = low; slotNum <= high; slotNum++) {
        const slot = String(slotNum);
        const existingItems = updatedLayout[slot];

        if (Array.isArray(existingItems) && existingItems.length > 0) continue;

        updatedLayout[slot] = [selectedItem];
      }

      onLayoutChange(updatedLayout);
      setRangeLow('');
      setRangeHigh('');
    } else {
      const slot = selectedLayout.trim();
      if (!slot) return;

      const currentLayout = layout[slot] || [];
      if (currentLayout.includes(selectedItem)) return;

      updatedLayout[slot] = [...currentLayout, selectedItem];
      onLayoutChange(updatedLayout);
      setSelectedLayout('');
    }
  };

  const handleRemoveFromLayout = (itemToRemove, slot) => {
    const currentLayout = layout[slot] || [];
    const updatedSlot = currentLayout.filter((item) => item !== itemToRemove);
    const updatedLayout = { ...layout };

    if (updatedSlot.length === 0) {
      delete updatedLayout[slot];
    } else {
      updatedLayout[slot] = updatedSlot;
    }

    onLayoutChange(updatedLayout);
  };

  const swapSlots = (key1, key2) => {
    if (!key1 || !key2) return;
    const updatedLayout = { ...layout };
    const temp = updatedLayout[key1];
    updatedLayout[key1] = updatedLayout[key2];
    updatedLayout[key2] = temp;
    onLayoutChange(updatedLayout);
  };

  const sortedSlots = Object.keys(layout)
    .map((k) => (isNaN(k) ? k : Number(k)))
    .sort((a, b) => {
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      return String(a).localeCompare(String(b));
    })
    .map(String);

  return (
    <div className="layout-settings">
      <div className="layout-controls">
        <div className="layout-controls-header">
          <h4>Selected {configType === 'inventory' ? 'Slot' : 'Index'}</h4>
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={rangeMode}
              onChange={() => setRangeMode(!rangeMode)}
            />
            Use Range
          </label>
        </div>

        {rangeMode ? (
          <div className="range-inputs">
            <input
              type="number"
              value={rangeLow}
              onChange={(e) => setRangeLow(e.target.value)}
              placeholder="Low"
              min="0"
            />
            <span>to</span>
            <input
              type="number"
              value={rangeHigh}
              onChange={(e) => setRangeHigh(e.target.value)}
              placeholder="High"
              min="0"
            />
          </div>
        ) : (
          <input
            type="text"
            value={selectedLayout}
            onChange={handleSelectedLayoutChange}
            placeholder="e.g., '0', '1', 'fill'"
            pattern="[a-zA-Z0-9%\{\}\[\]_-]*"
            title="Only letters, numbers, %, { }, [ ], _ and - are allowed"
          />
        )}

        <button
          onClick={handleAddToLayout}
          disabled={
            !selectedItem ||
            (rangeMode
              ? rangeLow === '' || rangeHigh === ''
              : selectedLayout.trim() === '')
          }
          className="add-button"
          type="button"
        >
          +
        </button>
      </div>

      <div className="layout-overview">
        {sortedSlots.length === 0 ? (
          <p style={{ fontStyle: 'italic', color: '#aaa' }}>
            No items assigned to any layout.
          </p>
        ) : (
          sortedSlots.map((slot, idx) => (
            <div key={slot} className="layout-slot">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <strong className="layout-slot-title">
                  {configType === 'inventory' ? 'Slot:' : 'Index:'} {slot}
                </strong>
                <div className="layout-move-buttons">
                  <button
                    onClick={() => swapSlots(slot, sortedSlots[idx - 1])}
                    disabled={idx === 0}
                    className="move-button"
                    type="button"
                    aria-label={`Move slot ${slot} up`}
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => swapSlots(slot, sortedSlots[idx + 1])}
                    disabled={idx === sortedSlots.length - 1}
                    className="move-button"
                    type="button"
                    aria-label={`Move slot ${slot} down`}
                  >
                    ▼
                  </button>
                </div>
              </div>
              <ul className="layout-item-list">
                {layout[slot].map((itemKey, index) => (
                  <li key={index} className="layout-item">
                    {itemKey}
                    <button
                      onClick={() => handleRemoveFromLayout(itemKey, slot)}
                      className="remove-button"
                      type="button"
                      aria-label={`Remove ${itemKey}`}
                    >
                      −
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
